import React from "react";
import type { SectionAboutTeaserProps } from "../../content/section-about-teaser";
import { useLocalizedUrl } from "../../hooks";
import { Button, ResponsiveImage, SectionHeader } from "../ui";

export type AboutTeaserSectionProps = SectionAboutTeaserProps & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Kapitel «Wer wir sind» der Startseite ist DIESE Ueberschrift die
     * Kapitelueberschrift — «Seit 2003 — und heute bauen wir anders» steht eine
     * Ebene hoeher in `ChapterSection`, «Was wir sonst noch machen» folgt
     * darunter als `h3`. Beide sagen, wer hier arbeitet; getrennt lasen sie
     * sich als zwei Abschnitte.
     *
     * `nested` laesst deshalb Ueberschrift und Badge weg und legt Flaeche und
     * Aussenabstand ab. Der Ausgang auf /ueber-uns bleibt.
     */
    nested?: boolean;
};

/**
 * «Wer wir sind» auf der Startseite (v0.3 F5).
 *
 * Der Teaser ersetzt die Ueber-uns-Seite nicht, er zeigt auf sie. Deshalb ein
 * Absatz und ein Ausgang — kein Team-Raster, keine Werte-Liste.
 */
export const AboutTeaserSection: React.FC<AboutTeaserSectionProps> = ({
    badge,
    title,
    description,
    image,
    imageAlt,
    cta,
    nested = false,
}) => {
    // Muss VOR jedem bedingten Ausstieg laufen (Rules of Hooks). Der Fallback
    // «/ueber-uns» ist die deutsche Kanonische — `useLocalizedUrl` uebersetzt
    // sie, sonst zeigte die englische Startseite auf die deutsche Seite.
    const href = useLocalizedUrl(cta?.href ?? "/ueber-uns");

    return (
        <section data-testid="about-teaser-section" className={nested ? "" : "bg-white py-24"}>
            {/*
                `px-4` wie die Sektionen darueber — das eigene `lg:px-16` zog den
                Teaser um 96px enger als den Rest der Seite (Jan, 5.8.2026).
            */}
            <div className="mx-auto w-full px-4 md:px-12">
                {/* Zwei Spalten nur MIT Bild — sonst haelftet die leere zweite
                    Spalte den Text. */}
                <div className={`grid items-center gap-12 ${image ? "md:grid-cols-2" : ""}`}>
                    {/*
                        Ausrichtung folgt dem Layout (Jan, 6.8.2026): MIT Bild steht
                        der Text linksbuendig neben der Abbildung — so war die Sektion
                        gebaut. OHNE Bild faellt das Zweispalten-Raster weg, der Block
                        laeuft ueber die volle Breite, und `text-left` liess den Badge
                        allein links unter einem Titel stehen, der die Breite fuellt.
                        Auf der Startseite ist kein Bild gesetzt — dort fiel es auf.

                        Nicht das `text-left` einfach entfernt: mit Bild waere
                        zentrierter Text neben einer Abbildung wieder falsch. Die
                        Bedingung haelt beide Faelle richtig.
                    */}
                    <div className={image ? "" : "mx-auto max-w-3xl"}>
                        {/* Im Kapitel traegt die Ueberschrift bereits das Kapitel —
                            hier stuende sie ein zweites Mal. */}
                        {!nested && (
                            <SectionHeader
                                badge={badge}
                                title={title}
                                className={`mb-6 ${image ? "text-left" : "text-center"}`}
                            />
                        )}
                        <p className={`mb-8 text-lg leading-relaxed text-black ${image ? "" : "text-center"}`}>
                            {description}
                        </p>
                        {/* `flex justify-center` statt `text-center`: `Button` ist
                            `inline-flex` und folgt zwar `text-align`, aber das steht
                            hier an den Geschwistern und nicht am gemeinsamen Elternteil
                            — ein eigener Wrapper ist eindeutiger als ein geerbtes
                            `text-center`, das jemand spaeter am `<p>` wegnimmt. */}
                        {cta?.text && (
                            <div className={image ? "" : "flex justify-center"}>
                                <Button href={href} variant="secondary" size="md">
                                    {cta.text}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Ohne Bild bleibt die Spalte leer statt gebrochen — ein
                        fehlendes Team-Bild macht den Teaser nicht wertlos. */}
                    {image && (
                        <div className="overflow-hidden rounded-2xl">
                            <ResponsiveImage
                                src={image}
                                alt={imageAlt ?? title}
                                className="h-auto w-full"
                                widths={[480, 768, 1024]}
                                sizes="(max-width: 768px) 100vw, 50vw"
                                objectFit="cover"
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default AboutTeaserSection;
